
import React, { useState, useCallback, useMemo, useRef, useContext } from 'react';
import ReactFlow, {
  Controls,
  Background,
  ConnectionMode,
  NodeTypes,
  useReactFlow,
  NodeMouseHandler,
} from 'reactflow';

import { Schedule, EntitlementEditorProps, ResourceRef, EntitlementObject, IRepositories } from '../types';
import ScheduleNode from './molecules/ScheduleNode';
import DetailsPanel from './organisms/details-panel';
import DeleteConfirmationModal from './molecules/DeleteConfirmationModal';
import GlobalSettingsModal from './organisms/GlobalSettingsModal';
import ResourceSelectionModal from './organisms/ResourceSelectionModal';
import ContextMenu from './atoms/ContextMenu';
import { useGraphController } from '../hooks/useGraphController';
import { LayoutDashboard, Plus, Settings as SettingsIcon, Save, ChevronRight, FileText, Undo2, Redo2, ArrowLeft, Download, Upload, LogOut } from 'lucide-react';

// Create a simple context for internal use only (backwards compatibility)
const RepositoryContext = React.createContext<IRepositories | null>(null);

const nodeTypes: NodeTypes = {
  scheduleNode: ScheduleNode,
};

const EntitlementEditor: React.FC<EntitlementEditorProps> = ({ 
    initialData, 
    onSave,
    onSaveAndReturn,
    onBack,
    additionalCanvasMenuItems,
    additionalNodeMenuItems,
    repositories: repositoriesProp
}) => {
  // Use prop directly if provided, otherwise try context (for backwards compatibility)
  const contextRepositories = useContext(RepositoryContext);
  const repositories = repositoriesProp || contextRepositories;

  // Logic Hook
  const {
      nodes,
      edges,
      onNodesChange,
      onEdgesChange,
      onConnect,
      handleUpdateNode,
      handleLayout,
      handleAddNode,
      setNodes,
      setEdges,
      undo,
      redo,
      canUndo,
      canRedo,
      takeSnapshot,
      loadData
  } = useGraphController(initialData);

  // Local UI State
  const [selectedNode, setSelectedNode] = useState<Schedule | null>(null);
  
  // Modals State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  
  const [nodesToDeleteCount, setNodesToDeleteCount] = useState(0);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; items: any[]; } | null>(null);
  
  const [globalSettingsOpen, setGlobalSettingsOpen] = useState(false);
  const [globalData, setGlobalData] = useState({
    name: initialData.name,
    tags: initialData.tags,
    configuration: initialData.configuration
  });

  const { getViewport, deleteElements, getNodes, getEdges } = useReactFlow();

  // Internal File I/O Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Interactions ---

  const onNodeClick = useCallback((event: React.MouseEvent, node: any) => {
    setSelectedNode(node.data as Schedule);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setContextMenu(null);
  }, []);

  const onNodeDragStart = useCallback(() => {
    // Capture state before a drag operation begins
    takeSnapshot();
  }, [takeSnapshot]);

  const handleCloseSidebar = () => {
    setSelectedNode(null);
    setNodes((nds) => nds.map((node) => ({ ...node, selected: false })));
  };

  const handleConfirmDelete = () => {
    takeSnapshot();
    const nodesToDelete = getNodes().filter(n => n.selected);
    const edgesToDelete = getEdges().filter(e => e.selected);
    deleteElements({ nodes: nodesToDelete, edges: edgesToDelete });
    setDeleteModalOpen(false);
    if (selectedNode && nodesToDelete.some(n => n.id === selectedNode.scheduleId)) {
        setSelectedNode(null);
    }
  };

  const handleConfirmAddResources = (newResources: ResourceRef[]) => {
      if (selectedNode) {
          const updatedResources = [...(selectedNode.resources || []), ...newResources];
          handleUpdateNode(selectedNode.scheduleId, 'resources', updatedResources);
      }
  };

  const getCurrentData = (): EntitlementObject => ({
      ...initialData, // Keep original ObjectType etc
      name: globalData.name,
      tags: globalData.tags,
      configuration: globalData.configuration,
      schedules: nodes.map(n => n.data as Schedule)
  });

  // --- File I/O Handlers ---

  const handleImportClick = () => {
      fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
          try {
              const content = e.target?.result as string;
              const parsed = JSON.parse(content) as EntitlementObject;
              if (!parsed.schedules || !Array.isArray(parsed.schedules)) {
                  alert("Invalid file format: Missing Schedules array.");
                  return;
              }
              // Update Global Data
              setGlobalData({
                  name: parsed.name || '',
                  tags: parsed.tags || [],
                  configuration: parsed.configuration || {}
              });
              // Update Graph Data
              loadData(parsed);
          } catch (error) {
              console.error(error);
              alert("Failed to load file.");
          }
      };
      reader.readAsText(file);
      event.target.value = ''; // Reset input
  };

  const handleExportClick = () => {
      const dataToExport = getCurrentData();
      const jsonString = JSON.stringify(dataToExport, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataToExport.name?.replace(/\s+/g, '_') || 'Entitlement'}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
  };


  const onNodeContextMenu: NodeMouseHandler = useCallback((event, node) => {
      event.preventDefault();
      
      const extraItems = additionalNodeMenuItems ? additionalNodeMenuItems(node.data) : [];
      const hasExtra = extraItems.length > 0;

      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        items: [
            ...extraItems,
            ...(hasExtra ? [{ label: 'Separator', disabled: true, onClick: () => {}, icon: <div className="h-px w-full bg-slate-200 my-1" /> }] : []),
            {
                label: 'Delete Node',
                icon: <FileText size={14} />, 
                onClick: () => {
                    setNodesToDeleteCount(1);
                    setNodes(nds => nds.map(n => ({ ...n, selected: n.id === node.id })));
                    setDeleteModalOpen(true);
                },
                danger: true
            }
        ].filter(item => item.label !== 'Separator' || item.icon) 
      });
  }, [setNodes, additionalNodeMenuItems]);

  const onPaneContextMenu = useCallback((event: React.MouseEvent) => {
      event.preventDefault();
      
      const extraItems = additionalCanvasMenuItems || [];
      const hasExtra = extraItems.length > 0;

      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        items: [
             {
                label: 'Add New Node Here',
                icon: <Plus size={14} />,
                onClick: () => {
                     handleAddNode(getViewport());
                }
            },
            ...(hasExtra ? [{ label: 'Separator', disabled: true, onClick: () => {}, icon: <div className="h-px w-full bg-slate-200 my-1" /> }] : []),
            ...extraItems
        ]
      });
  }, [handleAddNode, getViewport, additionalCanvasMenuItems]);

  // Sync selected node data if it changes in graph
  React.useEffect(() => {
      if (selectedNode) {
          const freshNode = nodes.find(n => n.id === selectedNode.scheduleId);
          if (freshNode) setSelectedNode(freshNode.data);
      }
  }, [nodes]);


  const currentSchedules = useMemo(() => nodes.map(n => n.data as Schedule), [nodes]);

  return (
    <div className="flex flex-col h-full w-full bg-slate-50 overflow-hidden">
      
      {/* Hidden File Input */}
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileChange} 
        accept=".json" 
        className="hidden" 
      />

      {/* --- HEADER BAR --- */}
      <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-2 md:px-6 shrink-0 z-50 shadow-sm relative gap-2">
         
         {/* Left: Branding & Title & Back */}
         <div className="flex items-center gap-2 md:gap-4 overflow-hidden z-20 min-w-0">
             {onBack && (
                 <>
                    <button 
                        onClick={onBack}
                        className="p-1.5 md:p-2 -ml-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div className="h-6 w-px bg-slate-200 mx-1 md:mx-2 hidden md:block"></div>
                 </>
             )}
             
             <div className="flex flex-col">
                 <h1 className="text-sm md:text-lg font-bold text-slate-900 truncate flex items-center gap-2">
                    {globalData.name || 'Untitled Entitlement'}
                 </h1>
                 <div className="flex items-center gap-2 text-[10px] md:text-xs text-slate-500">
                    <span className="bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 font-mono">
                        {nodes.length} Schedules
                    </span>
                    {globalData.tags.length > 0 && (
                        <span className="hidden md:inline-flex items-center gap-1">
                            <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                            {globalData.tags.join(', ')}
                        </span>
                    )}
                 </div>
             </div>
         </div>
 
         {/* Right: Actions */}
         <div className="flex items-center gap-1 md:gap-2">
             <div className="hidden md:flex items-center gap-1 mr-2 border-r border-slate-200 pr-2">
                 <button onClick={undo} disabled={!canUndo} className="p-2 text-slate-400 hover:text-slate-700 disabled:opacity-30 rounded-lg" title="Undo"><Undo2 size={18} /></button>
                 <button onClick={redo} disabled={!canRedo} className="p-2 text-slate-400 hover:text-slate-700 disabled:opacity-30 rounded-lg" title="Redo"><Redo2 size={18} /></button>
             </div>

             <button onClick={handleLayout} className="p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors hidden sm:flex" title="Auto Layout">
                 <LayoutDashboard size={18} />
             </button>
             
             <button onClick={() => setGlobalSettingsOpen(true)} className="p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Settings">
                 <SettingsIcon size={18} />
             </button>

             <div className="h-6 w-px bg-slate-200 mx-1"></div>
             
             <div className="flex items-center gap-1">
                <button onClick={handleImportClick} className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Import JSON">
                     <Upload size={18} />
                </button>
                <button onClick={handleExportClick} className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Export JSON">
                     <Download size={18} />
                </button>
             </div>

             <button 
                onClick={() => {
                    if (onSave) onSave(getCurrentData());
                }}
                className="ml-2 bg-teal-600 hover:bg-teal-700 text-white px-3 md:px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm shadow-teal-600/20 transition-all"
             >
                 <Save size={16} />
                 <span className="hidden md:inline">Save</span>
             </button>
             
             {onSaveAndReturn && (
                 <button 
                     onClick={() => onSaveAndReturn(getCurrentData())}
                     className="ml-1 p-2 text-teal-600 hover:bg-teal-50 rounded-lg"
                     title="Save & Return"
                 >
                     <LogOut size={18} />
                 </button>
             )}
         </div>
      </header>

      {/* --- MAIN CANVAS --- */}
      <div className="flex-1 relative bg-slate-50">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onNodeDragStart={onNodeDragStart}
          onNodeContextMenu={onNodeContextMenu}
          onPaneContextMenu={onPaneContextMenu}
          fitView
          minZoom={0.1}
          maxZoom={1.5}
          connectionMode={ConnectionMode.Loose}
          defaultEdgeOptions={{
              type: 'smoothstep',
              animated: false,
              style: { stroke: '#94a3b8', strokeWidth: 2 },
          }}
        >
          <Background color="#e2e8f0" gap={20} />
          <Controls className="!bg-white !shadow-sm !border !border-slate-200 !m-4" />
        </ReactFlow>

        {/* Context Menu */}
        {contextMenu && (
            <ContextMenu 
                x={contextMenu.x}
                y={contextMenu.y}
                items={contextMenu.items}
                onClose={() => setContextMenu(null)}
            />
        )}

        {/* Details Panel Sidebar */}
        {selectedNode && (
            <DetailsPanel 
                node={selectedNode}
                allNodes={currentSchedules}
                onClose={handleCloseSidebar}
                onUpdate={handleUpdateNode}
                onAddResourceRequest={() => setResourceModalOpen(true)}
            />
        )}
      </div>

      {/* --- MODALS --- */}
      
      <DeleteConfirmationModal 
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        count={nodesToDeleteCount}
      />

      <GlobalSettingsModal
        isOpen={globalSettingsOpen}
        onClose={() => setGlobalSettingsOpen(false)}
        data={globalData}
        onSave={(newData) => {
            setGlobalData(newData);
            // Also update graph name if needed or just sync
        }}
      />

      <ResourceSelectionModal
        isOpen={resourceModalOpen}
        onClose={() => setResourceModalOpen(false)}
        onConfirm={handleConfirmAddResources}
        repositories={repositories}
      />

    </div>
  );
};

export default EntitlementEditor;